"use client";
import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useFormik } from "formik";
import { ModuleSettingsStore } from "../../../../utils/contentstack/moduleSettingsStore.js";
import { ZodError } from "zod";
import { useId } from "react";
import { useTracking } from "react-tracking";
import { Box } from "@mui/material";
import { Text, Notification, TextField, InputError, Button } from "@nxt-costco-com/forge-components";
import { SpaceLg, SpaceXs, ColorGreen500, FontSize300 } from "@nxt-costco-com/forge-design-tokens";
import { Checkmark } from "@nxt-costco-com/forge-icons/icons/Checkmark";
import { COMPONENT_NAME } from "../constants.js";
import { getErrorAriaIconAttributes, compactFormValidationSchema } from "../lib.js";
import { createFetchProxy } from "../utils.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
const EmailSignUpFormUICompact = (props) => {
  const { trackEvent } = useTracking();
  const id = useId();
  const { data, successRate = 0.9, apiEndpointData, authToken } = props ?? {};
  const {
    key_value = [],
    composable_window = [],
    isUsingMockData = false
  } = data ?? {};
  const msStore = new ModuleSettingsStore({ composable_window, key_value });
  const inlineFormTitleContent = msStore.getKeyValueItem(
    "inlineformtitlecontent"
  );
  const inlineFormPlaceholderContent = msStore.getKeyValueItem(
    "inlineformplaceholdercontent"
  );
  const inlineFormButtonContent = msStore.getKeyValueItem(
    "inlineformbuttoncontent"
  );
  const emailError = msStore.getComposableWindowItem("emailError");
  const successContent = msStore.getComposableWindowItem("successContent");
  const apiFailureContent = msStore.getComposableWindowItem("apiFailureContent");
  const errorAriaLabelPrefix = msStore.getComposableWindowItem(
    "errorAriaLabelPrefix"
  );
  const textFieldId = `inlineEmailAddressField-${id}`;
  const textFieldLabelId = `inlineEmailAddressFieldLabel-${id}`;
  const titleId = `inlineEmailAddressTitle-${id}`;
  const validate = (values) => {
    const errors = {};
    try {
      compactFormValidationSchema.parse(values);
    } catch (error) {
      if (error instanceof ZodError) {
        const formattedError = error.format();
        if (formattedError.emailAddress) {
          errors.emailAddress = emailError;
        }
        const inputElem = document.getElementById(
          `inlineEmailAddressField-${id}`
        );
        if (inputElem) inputElem.focus();
      }
    }
    return errors;
  };
  const apiEndpointCall = createFetchProxy(
    { message: "Success.", status: 200 },
    {
      isNative: !isUsingMockData,
      maxDelayInMillis: 1e3,
      successRate
    }
  );
  const submitEmailAddress = async (emailAddress) => {
    var _a, _b;
    if (!apiEndpointData || !Object.keys(apiEndpointData).length) return;
    const {
      endpoint,
      method,
      required_request_headers: headers,
      required_request_parameters: body
    } = apiEndpointData ?? {};
    const listSource = (_b = (_a = apiEndpointData == null ? void 0 : apiEndpointData.custom_properties) == null ? void 0 : _a.listSource) == null ? void 0 : _b.inline;
    headers["Authorization"] = `Bearer ${authToken}`;
    body["listSource"] = listSource;
    body["emailAddress"] = emailAddress;
    body.consents.marketing.email["Time"] = (/* @__PURE__ */ new Date()).toISOString();
    await apiEndpointCall(
      endpoint,
      {
        body: JSON.stringify(body),
        headers,
        method
      }
    );
  };
  const onSubmit = async () => {
    try {
      await submitEmailAddress(formik.values.emailAddress);
      trackEvent({
        identifier: "emailSignup",
        keyword: "Footer",
        trackType: "search"
      });
      formik.setFieldValue("isSuccessful", true);
    } catch (error) {
      console.error(error);
      formik.setFieldValue("isFailure", true);
    }
  };
  const formik = useFormik({
    initialValues: {
      emailAddress: "",
      isFailure: false,
      isSuccessful: false,
      zipCode: ""
    },
    onSubmit,
    validate,
    validateOnChange: false
  });
  const focusSuccessMessage = (elem) => {
    elem == null ? void 0 : elem.focus();
  };
  const handleChange = (event) => {
    formik.setFieldValue("emailAddress", event.target.value);
  };
  const handleSubmit = (event) => {
    formik.validateForm().then(() => {
      event.preventDefault();
      formik.handleSubmit();
    });
  };
  const errorTextId = `${COMPONENT_NAME}_EmailSignUp_${formik.errors.emailAddress}`;
  const errorIconA11yAttributes = getErrorAriaIconAttributes(errorAriaLabelPrefix);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Text,
      {
        component: "h2",
        id: titleId,
        sx: { paddingBottom: "12px" },
        variant: "t4",
        children: inlineFormTitleContent
      }
    ),
    /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit, children: /* @__PURE__ */ jsxs(CostcoBox, { "data-testid": COMPONENT_NAME, sx: { width: "100%" }, children: [
      formik.values.isFailure && !formik.values.isSuccessful ? /* @__PURE__ */ jsx(
        Notification,
        {
          isRole: true,
          message: apiFailureContent,
          severity: "critical",
          sx: { mb: SpaceLg }
        }
      ) : null,
      formik.values.isSuccessful ? /* @__PURE__ */ jsxs(CostcoBox, { sx: { display: "flex" }, children: [
        /* @__PURE__ */ jsx(
          Box,
          {
            component: Checkmark,
            sx: {
              color: ColorGreen500,
              marginRight: SpaceXs
            }
          }
        ),
        /* @__PURE__ */ jsx(
          Text,
          {
            sx: {
              color: ColorGreen500,
              fontSize: FontSize300
            },
            id: "successConfirmation",
            ref: focusSuccessMessage,
            tabIndex: -1,
            variant: "t5",
            children: successContent
          }
        )
      ] }) : /* @__PURE__ */ jsx(
        TextField,
        {
          endAdornment: /* @__PURE__ */ jsx(
            Button,
            {
              "aria-label": `${inlineFormButtonContent} ${inlineFormTitleContent}`,
              onClick: handleSubmit,
              type: "submit",
              variant: "primary",
              children: inlineFormButtonContent
            }
          ),
          helperText: /* @__PURE__ */ jsx(
            InputError,
            {
              errorIconA11yAttributes,
              ariaLabelErrorPrefix: errorAriaLabelPrefix,
              errorText: emailError,
              errorTextId,
              hasError: !!formik.errors.emailAddress
            }
          ),
          sx: {
            minWidth: "100%"
          },
          autoComplete: "email",
          errorText: formik.errors.emailAddress,
          id: textFieldId,
          inputAriaLabelledBy: `${textFieldLabelId} ${titleId}`,
          inputLabelId: textFieldLabelId,
          isError: !!formik.errors.emailAddress,
          label: inlineFormPlaceholderContent,
          onChange: handleChange,
          size: "small",
          value: formik.values.emailAddress
        }
      )
    ] }) })
  ] });
};
export {
  EmailSignUpFormUICompact
};
